<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import { Gallery } from 'flowbite-svelte';
  import VideoPlayer from '$lib/components/VideoPlayer.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import { animate, inView } from '@motionone/dom';
  import IndustryTag from '$lib/components/IndustryTag.svelte';
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  
  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;

  let titleContent: HTMLElement;
  let viewsCount = 0;
  let languagesCount = 0;
  let yearsCount = 0;
  let ratingCount = 0;

  // Gallery image arrays
  const galleryColumn1 = [{ 
    src: "https://framerusercontent.com/images/BfHlsllzAV31I7gaFN8QG8brKvE.jpg",
    alt: "FactSnap Interface"
  }];

  const galleryColumn2 = [
    { 
      src: "https://framerusercontent.com/images/cRDyiqgVq3dtMGZBtBi4FE1E3w.jpg",
      alt: "Real-time Analysis"
    }
  ];

  const galleryColumn3 = [
    { 
      src: "https://framerusercontent.com/images/BfHlsllzAV31I7gaFN8QG8brKvE.jpg",
      alt: "Multi-language Support"
    }
  ];

  const galleryColumn4 = [
    { 
      src: "https://framerusercontent.com/images/cRDyiqgVq3dtMGZBtBi4FE1E3w.jpg",
      alt: "AI-Powered Features"
    }
  ];

  // Logo rotation animation
  let logoElements: HTMLElement[] = [];
  
  onMount(() => {
    // Setup number animations
    const metricsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateValue(0, 809, 2000, (val) => viewsCount = val);
          animateValue(0, 2, 1000, (val) => languagesCount = val);
          animateValue(0, 5, 1500, (val) => yearsCount = val);
          animateValue(0, 5, 1000, (val) => ratingCount = val);
          metricsObserver.disconnect();
        }
      });
    });

    const metricsSection = document.querySelector('.metrics-section');
    if (metricsSection) {
      metricsObserver.observe(metricsSection);
    }

    // Setup scroll animations
    setupProjectAnimations();

    // Setup logo animations using Motion One
    inView('.logo-container', (info) => {
      const target = info.target as HTMLElement;
      const index = Array.from(document.querySelectorAll('.logo-container')).indexOf(target);
      
      animate(
        target,
        { transform: ['rotate(-180deg)', 'rotate(0deg)'] },
        { 
          duration: 1,
          delay: index * 0.2,
          easing: [0.16, 1, 0.3, 1]
        }
      );
    }, { amount: 0.2 });

    return () => {
      metricsObserver.disconnect();
    };
  });
</script>

<style>
  :global(.prose) {
    max-width: none;
  }

  .logo-container {
    @apply relative;
    z-index: 10;
  }

  /* Stagger the animation delay for each logo */
  .logo-container:nth-child(1) { transition-delay: 0ms; }
  .logo-container:nth-child(2) { transition-delay: 200ms; }
  .logo-container:nth-child(3) { transition-delay: 400ms; }
  .logo-container:nth-child(4) { transition-delay: 600ms; }
</style>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <!-- Hero Section -->
  <ProjectHeroSplit
    title="FactSnap"
    client="AI4Democracy & Neil Patel Digital"
    industry="Technology & Media"
    product="AI-powered platform for instant fact checking"
    heroMedia={{
      type: 'image',
      src: "https://framerusercontent.com/images/BfHlsllzAV31I7gaFN8QG8brKvE.jpg"
    }}
    metrics={[
      { value: 809, label: "Users in First Month" },
      { value: 2, label: "Languages" },
      { value: 5, label: "Star Rating" }
    ]}
    roles="Creative lead, AI, fact checking, social media, and data analysis."
  />

  <!-- Content -->
  <div class="project-section">
    <!-- Project Description -->
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90 animate-on-view">
        Empowering users to verify information instantly with AI-driven fact checking
      </p>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">The Challenge</h2>
          <p class="mb-16 text-gray-700">
            In today's digital landscape, the rapid spread of misinformation poses a significant challenge. Users need a reliable way to quickly verify information across multiple platforms and languages. 
          </p>
        </div>
      </div>

      <div class="bg-gray-50 p-8 rounded-lg relative">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">The Solution</h2>
          <p class="mb-16 text-gray-700">
            FactSnap leverages advanced AI technology to provide instant fact-checking and verification services.
          </p>
        </div>
      </div>
    </div>

    <!-- Quote Section -->
    <section class="py-8 animate-on-view">
      <div class="container px-4 md:px-8 py-16 max-w-4xl">
        <div class="grid grid-cols-12">
          <div class="col-span-12">
            <div class="relative">
              <span 
                class="absolute -top-8 text-[120px] leading-none text-[#F96604] font-serif animate-on-view"
                aria-hidden="true"
                style="opacity: 0.1"
              >
                ”
              </span>
              <blockquote class="py-12 text-xl md:text-4xl font-light animate-on-view" style="color: hsla(226, 65%, 35%, 1); line-height: 1.4;">
                Our mission is to make fact-checking accessible and instant, empowering users to make informed decisions in an age of information overload.
              </blockquote>
              <footer class="text-sm mt-4 text-gray-600">— FactSnap Team</footer>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Key Features -->
    <div class="w-full">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Key Features</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 mb-16 bg-black">
        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="currentColor" d="M4 2v26h26v2H2V2h2zm24.586 4L24 10.586L19.414 6L14 11.414L10.586 8L6 12.586v3.828l4.586-4.586L14 15.414L19.414 10l4.586 4.586L29.414 9L28 7.586z"/></svg>'
          title="Instant Verification"
          description="Real-time fact-checking using advanced AI algorithms and trusted sources"
          delay={0}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="currentColor" d="M28 4H4a2 2 0 00-2 2v20a2 2 0 002 2h24a2 2 0 002-2V6a2 2 0 00-2-2zm0 2v4H4V6h24zm-24 8h24v12H4V14z"/></svg>'
          title="Comprehensive Analysis"
          description="Deep content analysis with source verification and context evaluation"
          delay={0.2}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path fill="currentColor" d="M16 2a14 14 0 1014 14A14 14 0 0016 2zm0 26a12 12 0 1112-12 12 12 0 01-12 12z"/></svg>'
          title="Multi-Platform Support"
          description="Seamless integration with major social media and content platforms"
          delay={0.4}
        />
      </div>
    </div>

    <!-- Additional Features Section -->
    <div class="w-full mb-16">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">How It Works</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 px-8">
        <div class="bg-gray-50 p-8 rounded-lg">
          <h3 class="text-xl font-medium mb-4">Input Processing</h3>
          <ul class="space-y-4">
            <li class="flex items-start">
              <span class="w-2 h-2 mt-2 bg-[#F96604] rounded-full mr-3"></span>
              <p>Upload or paste content for verification</p>
            </li>
            <li class="flex items-start">
              <span class="w-2 h-2 mt-2 bg-[#F96604] rounded-full mr-3"></span>
              <p>Support for text, images, and social media posts</p>
            </li>
            <li class="flex items-start">
              <span class="w-2 h-2 mt-2 bg-[#F96604] rounded-full mr-3"></span>
              <p>Multiple language support for global reach</p>
            </li>
          </ul>
        </div>

        <div class="bg-gray-50 p-8 rounded-lg">
          <h3 class="text-xl font-medium mb-4">Verification Process</h3>
          <ul class="space-y-4">
            <li class="flex items-start">
              <span class="w-2 h-2 mt-2 bg-[#F96604] rounded-full mr-3"></span>
              <p>AI-powered analysis of content authenticity</p>
            </li>
            <li class="flex items-start">
              <span class="w-2 h-2 mt-2 bg-[#F96604] rounded-full mr-3"></span>
              <p>Cross-reference with trusted sources</p>
            </li>
            <li class="flex items-start">
              <span class="w-2 h-2 mt-2 bg-[#F96604] rounded-full mr-3"></span>
              <p>Detailed verification reports with sources</p>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Impact Section -->
    <div class="w-full animate-on-view">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Impact & Results</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 mb-16 bg-black">
        <!-- Results -->
        <div class="relative">
          <FeatureCell
            icon=""
            title="User Growth"
            description="Over 800 active users in the first month with consistent growth"
            delay={0.2}
          />
          <FeatureCell
            icon=""
            title="Verification Accuracy"
            description="95% accuracy rate in fact verification across multiple languages"
            delay={0.4}
          />
        </div>

        <!-- Implementation -->
        <div class="relative">
          <FeatureCell
            icon=""
            title="User Satisfaction"
            description="Maintained a 5-star rating with positive user feedback"
            delay={0.6}
          />
          <FeatureCell
            icon=""
            title="Platform Integration"
            description="Successfully integrated with major social media platforms"
            delay={0.8}
          />
        </div>
      </div>
    </div>

    <!-- Interface Gallery -->
    <div class="w-full mb-16">
      <h2 class="text-2xl font-light mt-16 mb-12 px-8">Interface Gallery</h2>
      
      <!-- Verification States -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 px-8 mb-16">
        <div class="bg-white p-6 rounded-xl shadow-xl">
          <div class="flex items-center space-x-2 mb-4 bg-[#FF6B6B] text-white px-4 py-2 rounded-lg">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            <span class="font-mono">THIS STATEMENT LOOKS INCORRECT</span>
          </div>
          <p class="text-sm text-gray-600">Used when a claim is demonstrably false based on available evidence</p>
        </div>

        <div class="bg-white p-6 rounded-xl shadow-xl">
          <div class="flex items-center space-x-2 mb-4 bg-gray-500 text-white px-4 py-2 rounded-lg">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            <span class="font-mono">THIS STATEMENT LOOKS SUSPICIOUS</span>
          </div>
          <p class="text-sm text-gray-600">Applied to claims that require further investigation or context</p>
        </div>

        <div class="bg-white p-6 rounded-xl shadow-xl">
          <div class="flex items-center space-x-2 mb-4 bg-[#4CAF50] text-white px-4 py-2 rounded-lg">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span class="font-mono">THIS STATEMENT LOOKS CORRECT</span>
          </div>
          <p class="text-sm text-gray-600">Indicates claims that are supported by reliable evidence</p>
        </div>
      </div>

      <!-- Example Verifications -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 px-8">
        <div class="bg-white rounded-xl shadow-xl overflow-hidden">
          <div class="p-6">
            <h3 class="text-xl font-semibold mb-4">Climate Change Verification</h3>
            <div class="bg-[#FF6B6B] text-white px-4 py-2 rounded-lg mb-4">
              <p class="font-mono">"Myth: Global warming isn't real as it is still cold"</p>
            </div>
            <p class="text-gray-600 text-sm">
              Comprehensive analysis of climate data and scientific consensus, with references to NASA and IPCC findings.
            </p>
          </div>
          <img 
            src="https://framerusercontent.com/images/BfHlsllzAV31I7gaFN8QG8brKvE.jpg" 
            alt="Climate Change Fact Check" 
            class="w-full h-64 object-cover"
          />
        </div>

        <div class="bg-white rounded-xl shadow-xl overflow-hidden">
          <div class="p-6">
            <h3 class="text-xl font-semibold mb-4">Political Statement Analysis</h3>
            <div class="bg-gray-500 text-white px-4 py-2 rounded-lg mb-4">
              <p class="font-mono">Political claim verification example</p>
            </div>
            <p class="text-gray-600 text-sm">
              Detailed breakdown of political statements with context and multiple trusted sources.
            </p>
          </div>
          <img 
            src="https://framerusercontent.com/images/cRDyiqgVq3dtMGZBtBi4FE1E3w.jpg" 
            alt="Political Fact Check" 
            class="w-full h-64 object-cover"
          />
        </div>
      </div>
    </div>

    <!-- Brand Identity -->
    <div class="w-full mb-16 bg-gray-50 py-16">
      <div class="container px-8">
        <h2 class="text-2xl font-light mb-12">Brand Identity</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
          {#each Array(4) as _, i}
            <div 
              class="logo-container"
              bind:this={logoElements[i]}
            >
              <img 
                src="/projects/fact-snap/icon-{i + 1}.svg" 
                alt="FactSnap logo variation {i + 1}" 
                class="w-full h-auto aspect-square max-w-[80px]"
              />
            </div>
          {/each}
        </div>
        <div class="mt-12 text-sm text-gray-600 text-center max-w-2xl mx-auto">
          <p class="mb-4">Our logo system combines classic typography with modern verification indicators</p>
          <div class="flex items-center justify-center gap-2">
            <span class="w-2 h-2 rounded-full bg-[#FF6B6B]"></span>
            <span class="w-2 h-2 rounded-full bg-[#808080]"></span>
            <span class="w-2 h-2 rounded-full bg-[#4CAF50]"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Mission Statement -->
    <div class="w-full mb-16">
      <div class="container px-8 max-w-4xl mx-auto">
        <h2 class="mb-8">Purpose</h2>
        <div class="grid grid-cols-1 gap-8">
          <p class="text-black/90 leading-relaxed">
            In an age of widespread misinformation, FactSnap empowers individuals to navigate the digital world critically. By providing a way to verify what people read, we aim to raise awareness, encourage thoughtful questioning, and inspire informed, independent opinions without claiming to hold the ultimate truth.
          </p>
          <p class="text-black/90 leading-relaxed">
            We believe that fostering critical thinking is key to combating the spread of fake news and promoting a healthier, more informed society. FactSnap doesn't dictate what to believe; instead, it provides the resources and context needed to examine claims, understand diverse perspectives, and make decisions based on evidence and reason.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Project Navigation -->
  <ProjectNav 
    prevProject={{ title: "Made to Measure", url: "/projects/made-to-measure" }}
    nextProject={{ title: "Project Soli", url: "/projects/project-soli" }}
  />

  <Footer />
</div> 